/**
 * 再認証（大事な操作の直前に「本人がいま操作している」ことを確かめる）の共通の部品（2026-09-27 creator/dev-oauth-ui）。
 * 使う所: /account の閉鎖・ログイン方法の追加 / 解除（Creator）、パスキーの登録 / 削除（Money の PasskeysPage）。
 *   パスワードの設定・変更には使わない（2026-09-28 Core SEC-DEVLOGIN-04 の (B): 変更は今のパスワードだけ・パスワードの無い人はメールのリンク）。
 *
 * サーバーの正: apps/server/src/lib/developerReauth.ts。操作の本文に `{ password }` か `{ reauthToken }` を足して送る。
 * 足りない / 合わないとき 400 reauthentication_required / 401 reauthentication_failed が返り、details = `{ hasPassword, providers }`
 * （この人が使える確認の方法。ログイン用パスキーを持つ人は `loginPasskey: true` も）。このダイアログは details を見て「パスワード欄」か「Google で確認」等のボタンを出し分ける。
 *
 * 使い方（呼んだ側）:
 *   const reauth = useReauth();
 *   // A. 送って、断られたら確かめて送り直す（いちばん簡単）
 *   const r = await reauth.run((proof) => api(path, { method: 'DELETE', body: { ...proof } }), { action: 'unlink:google' });
 *   // B. 自分で聞く: const proof = await reauth.requestReauth(err か { hasPassword, providers }, { action }); → { password } | { reauthToken } | null
 *   // ダイアログの置き場: JSX のどこかに {reauth.dialog}
 *   // 断られた理由が再認証か: `import { isReauthError } from '../components/Reauth'`（**フックの戻り値ではなく、別の名前付きの export**）
 *
 * 部品がダイアログを出すのは、400 reauthentication_required / 401 reauthentication_failed のときだけ。ほかのコードで
 *   「確認が要る」を表す口（例 Money のパスキー削除の 403 passkey_confirmation_required）は、呼んだ側で
 *   reauthentication_required の形（details の { hasPassword, providers }）に読み替えてから requestReauth に渡す
 *   （Money の PasskeysPage がそうしている・2026-09-28 Money の報告）。
 *
 * 「Google で確認」はプロバイダの画面へ移る = この画面の途中の状態（Promise・入力）はいったん消える。
 *   控え（state・verifier・戻る画面 returnTo・操作の名前 action）は sessionStorage（devOAuth.ts の PendingOAuth）、
 *   戻ったら /oauth/callback が reauthToken を受け取り、**メモリだけ**に置いて returnTo へ戻す（devOAuthClient.ts の setResumedReauth）。
 *   戻った画面では `reauth.pending`（{ action, reauthToken, expiresAt, provider } | null）に入っている。続きは呼んだ側が決める:
 *     - そのまま自動で続けてよい操作（解除・閉鎖の送信・削除の確認）→ `reauth.run(send, { action })` が pending を自分で使う
 *     - ユーザーの操作が要る操作（パスキーの登録 = navigator.credentials.create はクリックの中でしか呼べない）→ ボタンをもう一度押してもらい、
 *       押したときに `reauth.takePending(action)` で取り出して本文に `{ reauthToken }` を足す
 *   token は 5 分・一回限り。使ったら（takePending / run が使う）消える。期限が切れたら pending は null に戻る。
 * 戻りで失敗したとき（別の Google アカウントを選んだ = reauth_mismatch・キャンセル）は `reauth.failure`（{ action, code, provider }・文言は useReauthFailureText()）。
 *
 * 「パスキーで確認」（2026-09-28 creator/passkey-login）: 方法に loginPasskey: true（サーバーの details・ログイン用パスキーを持つ人だけ）が
 *   あり、ブラウザが WebAuthn を持つときに出す。押す → POST …/login-passkeys/reauth/options → navigator.credentials.get →
 *   POST …/login-passkeys/reauth → { reauthToken } を `{ reauthToken }` の証明として返す（パスワードと同じ強さ・5 分・一回限り）。
 *   ページを移らないので、戻りの控え（pending）は使わない = Promise にそのまま返る。プロンプトを閉じた（NotAllowedError）ときは何も出さず、
 *   ダイアログは開いたまま（ほかの方法を選べる）。パスキーだけの人（パスワードも外部 ID も無い）は、これが唯一の確認の方法。
 *   Money の PasskeysPage も同じ部品なので、方法（ReauthMethods）に loginPasskey を足せば同じボタンが出る（呼んだ側の変更は方法の組み立てだけ）。
 *   注意: WebAuthn の create（パスキーの登録）を、この確認のすぐ後に続けて呼ぶ画面は、確認の get と create が続けて出ることになる。
 *   Safari はクリックから時間の空いた create を断ることがあるので、登録の画面では「確認済み。もう一度押してください」を挟む
 *   （pages/AccountLoginPasskeys.tsx の add の WHY）。
 *
 * WHY 自動で送り直さないか（部品の側で）: パスキーの登録のように「戻った直後には実行できない」操作があり、
 *   閉鎖のように取り消せない操作は最後の 1 回を本人に押させたい場面もある。部品は証明を渡すまで、続きは呼んだ側（Money の要望 2026-09-27）。
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import type { ApiError } from '../api';
import { authFormProps, hiddenUsernameProps, passwordFieldProps } from '../authForms';
import { isReauthError, normalizeCallbackError, PROVIDER_NAMES, reauthMethodsFrom, type ReauthMethods, type ReauthProof, type ResumedReauth } from '../devOAuth';
import { clearResumed, onResumedChange, peekResumedFailure, peekResumedReauth } from '../devOAuthClient';
import { useT, type MessageKey } from '../i18n';
import { reauthWithLoginPasskey } from '../loginPasskeyClient';
import { passkeyLoginError } from '../loginOptions';
import { passkeysSupported } from '../passkey';
import { useSession } from '../session';
import { PasskeyIcon, ProviderButton, useOAuthProviders, useStartOAuth } from './OAuthButtons';

/** 確認の方法の出どころ: サーバーのエラー（details つき）か、先に分かっている方法（GET /v1/developers/me/identities 等） */
export type ReauthSource = ReauthMethods | Pick<ApiError, 'code' | 'details'>;

export interface ReauthRequestOptions {
  /** 戻った後に続ける操作の名前（例 'close'・'unlink:google'・'passkey:add'）。呼んだ側が pending.action と比べる */
  action: string;
  /** 「Google で確認」から戻る画面。既定 = 今の画面（パス + クエリ。# は持たない = 出金の確認のトークン等を控えに残さない） */
  returnTo?: string;
}

interface OpenRequest {
  methods: ReauthMethods;
  failed: boolean;
  action: string;
  returnTo: string;
  resolve: (p: ReauthProof | null) => void;
}

const isMethods = (s: ReauthSource): s is ReauthMethods => typeof (s as ReauthMethods).hasPassword === 'boolean' && Array.isArray((s as ReauthMethods).providers);

export function useReauth() {
  const location = useLocation();
  const [req, setReq] = useState<OpenRequest | null>(null);
  const [pending, setPending] = useState<ResumedReauth | null>(() => peekResumedReauth());
  const [failure, setFailure] = useState(() => peekResumedFailure());
  const reqRef = useRef<OpenRequest | null>(null);
  reqRef.current = req;

  useEffect(
    () =>
      onResumedChange(() => {
        setPending(peekResumedReauth());
        setFailure(peekResumedFailure());
      }),
    []
  );
  // 期限が来たら pending を消す（押せるボタンを出したままにしない）
  useEffect(() => {
    if (!pending) return;
    const ms = Date.parse(pending.expiresAt) - 10_000 - Date.now();
    const id = setTimeout(() => setPending(peekResumedReauth()), Math.max(0, ms) + 50);
    return () => clearTimeout(id);
  }, [pending]);
  // 画面を離れたら開いたままの問いは null で閉じる（Promise を宙に残さない）
  useEffect(() => () => reqRef.current?.resolve(null), []);

  const requestReauth = useCallback(
    (source: ReauthSource, opts: ReauthRequestOptions): Promise<ReauthProof | null> =>
      new Promise((resolve) => {
        reqRef.current?.resolve(null);
        const methods = isMethods(source) ? source : reauthMethodsFrom(source.details);
        const failed = !isMethods(source) && source.code === 'reauthentication_failed';
        setReq({ methods, failed, action: opts.action, returnTo: opts.returnTo ?? location.pathname + location.search, resolve });
      }),
    [location.pathname, location.search]
  );

  /** 戻ってきた証明を action で取り出す（一回限り = 取り出したら消える）。無い・期限切れ・別の操作なら null */
  const takePending = useCallback((action: string): string | null => {
    const p = peekResumedReauth();
    if (!p || p.action !== action) return null;
    clearResumed();
    return p.reauthToken;
  }, []);

  /** 戻ってきた証明・失敗を捨てる（使わずに画面を閉じる等） */
  const consume = useCallback(() => clearResumed(), []);

  /**
   * 送る → 再認証を求められたらダイアログで確かめる → 送り直す。取り消したら null。
   * 戻ってきた証明（同じ action）があれば、最初の送信にそれを使う（自動で続ける場面）。
   * ask を渡すと、最初の送信の前に聞く（方法が先に分かっている = 往復を 1 回減らす）
   */
  const run = useCallback(
    async <T,>(send: (proof: ReauthProof | Record<string, never>) => Promise<T>, opts: ReauthRequestOptions & { ask?: ReauthMethods }): Promise<T | null> => {
      const resumedToken = takePending(opts.action);
      let proof: ReauthProof | Record<string, never> = resumedToken ? { reauthToken: resumedToken } : {};
      if (!resumedToken && opts.ask) {
        const p = await requestReauth(opts.ask, opts);
        if (!p) return null;
        proof = p;
      }
      // 打ち間違いの繰り返しは、サーバーの回数の制限（1 時間 5 回等）が止める。ここは無限に回らないための上限だけ
      for (let i = 0; i < 6; i++) {
        try {
          return await send(proof);
        } catch (err) {
          if (!isReauthError(err)) throw err;
          const p = await requestReauth(err as ApiError, opts);
          if (!p) return null;
          proof = p;
        }
      }
      return null;
    },
    [requestReauth, takePending]
  );

  const close = (p: ReauthProof | null) => {
    req?.resolve(p);
    setReq(null);
  };

  const dialog = req ? <ReauthDialog key={`${req.action}:${req.failed}`} request={req} onDone={close} /> : null;

  return {
    requestReauth,
    run,
    pending,
    takePending,
    consume,
    /** 戻りで失敗したときの { action, code }（code は devOAuth.ts の CallbackError）。文言は useReauthFailureText() */
    failure,
    dialog
  };
}

/** 戻りの失敗の文言（reauth_mismatch = 別のアカウントを選んだ・access_denied = キャンセル 等） */
export function useReauthFailureText() {
  const { t } = useT();
  return (f: { code: string; provider?: string }) =>
    t(`oauth.error.${normalizeCallbackError(f.code)}` as MessageKey, { provider: f.provider && f.provider in PROVIDER_NAMES ? PROVIDER_NAMES[f.provider as keyof typeof PROVIDER_NAMES] : 'Google' });
}

/**
 * ダイアログ本体。パスワードを持つ人 = 今のパスワードの欄（パスワード管理が入れられる形: post のフォーム・見えないアカウント名・
 * current-password）。付いているプロバイダ = 「Google で確認」等のボタン（サーバーの oauthProviders にもあるものだけ）。
 */
function ReauthDialog({ request, onDone }: { request: OpenRequest; onDone: (p: ReauthProof | null) => void }) {
  const { t } = useT();
  const { developer } = useSession();
  const configured = useOAuthProviders();
  const { start, busy, error: startError } = useStartOAuth();
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const [password, setPassword] = useState('');
  const { methods, failed } = request;
  // サーバーの設定から外れたプロバイダ（env を消した）は押しても 404 になるので出さない。一覧が読めない間は details のまま
  const providers = configured.length ? methods.providers.filter((p) => configured.includes(p)) : methods.providers;
  // ログイン用パスキーでの確認（ブラウザが WebAuthn を持たない端末では出さない = 押しても必ず失敗する）
  const passkey = !!methods.loginPasskey && passkeysSupported();
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    d?.querySelector<HTMLInputElement>('input[type=password]')?.focus();
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!password) return;
    onDone({ password });
  };

  const confirmWithPasskey = async () => {
    setPasskeyError(null);
    setPasskeyBusy(true);
    try {
      const r = await reauthWithLoginPasskey();
      onDone({ reauthToken: r.reauthToken });
    } catch (err) {
      // 閉じた / 時間切れは何も出さない（ダイアログは開いたまま = ほかの方法を選べる）
      if (passkeyLoginError(err) !== 'cancel') setPasskeyError(t('reauth.passkeyFailed'));
      setPasskeyBusy(false);
    }
  };

  const confirmWith = (provider: (typeof providers)[number]) =>
    void start({ provider, intent: 'reauth', returnTo: request.returnTo, action: request.action }).catch(() => undefined);

  return (
    <dialog ref={ref} className="dialog" aria-labelledby={`${id}-t`} onClose={() => onDone(null)} onCancel={() => onDone(null)}>
      <form {...authFormProps('reauth', request.returnTo)} onSubmit={submit}>
        <h2 id={`${id}-t`}>{t('reauth.title')}</h2>
        <div className="dialog-body">
          <p>{t('reauth.lead')}</p>
          {failed && (
            <p className="alert alert-error" role="alert">
              {t('reauth.failed')}
            </p>
          )}
          {methods.hasPassword && (
            <>
              {developer && <input {...hiddenUsernameProps(developer.email)} />}
              <label className="field" htmlFor="reauth-password">
                <span>{t('reauth.password')}</span>
                <input {...passwordFieldProps('current', { id: 'reauth-password' })} value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
            </>
          )}
          {providers.length > 0 && (
            <div className="oauth-list">
              {methods.hasPassword && (
                <div className="auth-divider" role="separator">
                  <span>{t('oauth.or')}</span>
                </div>
              )}
              {providers.map((p) => (
                <ProviderButton
                  key={p}
                  provider={p}
                  label={busy === p ? t('auth.busy') : t('reauth.confirmWith', { provider: PROVIDER_NAMES[p] })}
                  disabled={!!busy || passkeyBusy}
                  onClick={() => confirmWith(p)}
                />
              ))}
              <p className="small muted">{t('reauth.oauthNote')}</p>
            </div>
          )}
          {passkey && (
            <div className="oauth-list reauth-passkey">
              {(methods.hasPassword || providers.length > 0) && (
                <div className="auth-divider" role="separator">
                  <span>{t('oauth.or')}</span>
                </div>
              )}
              <button type="button" className="btn btn-block oauth-btn oauth-passkey" disabled={!!busy || passkeyBusy} onClick={() => void confirmWithPasskey()}>
                <PasskeyIcon />
                <span>{passkeyBusy ? t('auth.busy') : t('reauth.passkey')}</span>
              </button>
              <p className="small muted">{t('reauth.passkeyNote')}</p>
              {passkeyError && (
                <p className="alert alert-error" role="alert">
                  {passkeyError}
                </p>
              )}
            </div>
          )}
          {!methods.hasPassword && providers.length === 0 && !passkey && <p className="alert alert-warn">{t('reauth.noMethod')}</p>}
          {startError && (
            <p className="alert alert-error" role="alert">
              {startError}
            </p>
          )}
        </div>
        <div className="dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={() => onDone(null)} data-cancel>
            {t('common.cancel')}
          </button>
          {methods.hasPassword && (
            <button type="submit" className="btn btn-primary" disabled={!password || !!busy || passkeyBusy}>
              {t('reauth.confirm')}
            </button>
          )}
        </div>
      </form>
    </dialog>
  );
}

/** エラーが再認証のものか（呼んだ側の分岐用の再輸出） */
export { isReauthError };
